/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import MuiDialogTitle from '@mui/material/DialogTitle';
import {
  IconButton,
  TextField,
  Typography,
  LinearProgress,
  FormControlLabel,
  Switch,
  Grid, Tooltip,
} from '@mui/material';
import { parse, isValid } from 'date-fns';
import BrokerDatePicker from '../../common/BrokerDatePicker';
import { renderHiddenInput } from '../../common/FormHiddenField';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import withStyles from '@mui/styles/withStyles';
import SearchIcon from '@mui/icons-material/Search';
import * as yup from 'yup';
import moment from 'moment';
import useAxios from 'axios-hooks';

import { postEmployee, updateEmployee } from '../actions/administrate_users';
import { checkEmptyObject, usePrevious } from '../../../utils/CommonUtils';
import { addSweetAlert, closeSweetAlert } from '../../common/SweetAlert/actions/sweet_alert';
import { isValidId } from '../../../utils/id-validator';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
          size="large">
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const schema = yup.object().shape({
  id: yup.string(),
  displayName: yup.string(),
  givenName: yup.string().required('El campo Nombre es requerido'),
  surName: yup.string().required('El campo Apellido es requerido'),
  email: yup.string().email('email no valido').required('El campo email es requerido'),
  agentIdentification: yup.string().test('id-validation', 'C\u00E9dula no es v\u00e1lida.', isValidId),
  birthday: yup.string(),
  accountEnabled: yup.boolean(),
});

const NewUserScreen = (props) => {
  const { open, handleClose, data } = props;
  const [readOnly, setReadOnly] = React.useState(true);
  const employees = useSelector((state) => state.employees);
  const dispatch = useDispatch();
  const [{ data: userData, error: userError }, fetchUser] = useAxios(
    null, { manual: true },
  );
  const previousEmployess = usePrevious(employees);

  const { handleSubmit, formState: { errors }, control, setValue, getValues, setError } = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });

  const defaultBirthday = data.birthday ? moment(data.birthday, 'DD/MM/yyyy') : null;
  const [birthdayForm, setBirthdayForm] = useState(defaultBirthday);

  // HANDLE SUCCESS
  useEffect(() => {
    if (previousEmployess && (previousEmployess.isSuccess !== employees.isSuccess)) {
      if (employees.isSuccess) {
        handleClose(true);
      }
    }
  }, [employees.isSuccess]);

  // HANDLE ERROR
  useEffect(() => {
    if (previousEmployess && (previousEmployess.error !== employees.error)) {
      if (employees.error) {
        dispatch(addSweetAlert({
          type: 'warning',
          title: 'No se pudo crear o Actualizar el Agente.',
          content: employees.errorData,
          showCancel: false,
          onConfirmLabel: 'Aceptar',
          onConfirm: () => {
            dispatch(closeSweetAlert());
          },
        }));
        handleClose(false);
      }
    }
  }, [employees.error]);

  const getUser = () => {
    const formValues = getValues();
    if (formValues.agentIdentification && !errors?.hasOwnProperty('agentIdentification')) {
      fetchUser({ url: `${process.env.REACT_APP_FIND_BY_IDENTIFICATION_URL}/${formValues.agentIdentification}` });
    }
  };

  useEffect(() => {
    if (!checkEmptyObject(userError)) {
      if (userError.response.status === 404) {
        setError('agentIdentification', 'notMatch', userError.response.data?.Message
          .concat('. Ingrese Manualmente.'));
        setReadOnly(false);
      } else {
        setReadOnly(true);
        setError('agentIdentification', 'notMatch', userError.response.data?.Detail || 'Error al obtener los datos');
      }
    }
  }, [userError, setError]);

  useEffect(() => {
    if (userData) {
      setValue('givenName', userData.name);
      setValue('surName', userData.lastName);
      setBirthdayForm(userData.birthDate);
      setReadOnly(userData.isValidatedCivilRegistry);
    }
  }, [userData, setValue]);

  const onSubmit = (values) => {
    if (!checkEmptyObject(data)) {
      dispatch(updateEmployee(values));
    } else {
      dispatch(postEmployee(values));
    }
  };

  function onChangeIdentification(event) {
    setValue('givenName', '');
    setValue('surName', '');
    setBirthdayForm(null);
    setReadOnly(true);
    return event.target?.value;
  }

  return (
    <div>
      <Dialog
        open={open}
        onClose={() => handleClose(false)}
        aria-labelledby="customized-dialog-title"
        fullWidth
      >
        <DialogTitle id="customized-dialog-title" onClose={() => handleClose(false)}>
          {data.id ? 'Editar' : 'Agregar'} Usuario
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Grid container spacing={1}>
              <Controller
                name="id"
                control={control}
                defaultValue={data.id}
                render={renderHiddenInput}
              />
              <Grid container item xs={12} sm={6} md={6} xl={6} lg={6}>
                <Controller
                  name="agentIdentification"
                  control={control}
                  defaultValue={data.agentIdentification || ''}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      margin="dense"
                      label="Cédula"
                      type="text"
                      fullWidth
                      onChange={(event) => { field.onChange(onChangeIdentification(event)); }}
                      error={errors?.hasOwnProperty('agentIdentification')}
                      helperText={errors.agentIdentification?.message}
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="Buscar" arrow>
                            <IconButton
                              aria-label="search"
                              size="small"
                              onClick={getUser}
                            >
                              <SearchIcon />
                            </IconButton>
                          </Tooltip>
                        ),
                        inputProps: { maxLength: 10 },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid container item alignItems="flex-end" xs={12} sm={6} md={6} xl={6} lg={6}>
                <Controller
                  name="birthday"
                  control={control}
                  defaultValue={data.birthday || ''}
                  render={({ field }) => (
                    <BrokerDatePicker
                      disableFuture
                      margin="dense"
                      label="Fecha de Nacimiento"
                      name={field.name}
                      fullWidth
                      format="dd/MM/yyyy"
                      value={birthdayForm && (birthdayForm.toDate ? birthdayForm.toDate() : birthdayForm)}
                      onChange={(formattedDate) => {
                        if (!formattedDate) {
                          setBirthdayForm(null);
                          field.onChange('');
                          return;
                        }
                        const parsed = parse(formattedDate, 'dd/MM/yyyy', new Date());
                        if (isValid(parsed)) {
                          setBirthdayForm(parsed);
                          field.onChange(formattedDate);
                        }
                      }}
                      onBlur={field.onBlur}
                      InputProps={{ readOnly }}
                      readOnly={readOnly}
                    />
                  )}
                />
              </Grid>
              <Grid container item xs={12} sm={6} md={6} xl={6} lg={6}>
                <Controller
                  name="givenName"
                  control={control}
                  defaultValue={data.givenName || ''}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      autoFocus
                      margin="dense"
                      label="Nombres"
                      type="text"
                      fullWidth
                      error={errors?.hasOwnProperty('givenName')}
                      helperText={errors.givenName?.message}
                      InputProps={{ readOnly }}
                    />
                  )}
                />
              </Grid>
              <Grid container item xs={12} sm={6} md={6} xl={6} lg={6}>
                <Controller
                  name="surName"
                  control={control}
                  defaultValue={data.surName || ''}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      margin="dense"
                      label="Apellidos"
                      type="text"
                      fullWidth
                      error={errors?.hasOwnProperty('surName')}
                      helperText={errors.surName?.message}
                      InputProps={{ readOnly }}
                    />
                  )}
                />
              </Grid>
              <Grid container item xs={12} sm={6} md={6} xl={6} lg={6}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue={data.email || ''}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      margin="dense"
                      label="Email"
                      type="email"
                      fullWidth
                      error={errors?.hasOwnProperty('email')}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Grid>
              <Grid container item xs={12} sm={6} md={6} xl={6} lg={6}>
                <Controller
                  name="accountEnabled"
                  control={control}
                  defaultValue={data.hasOwnProperty('accountEnabled') ? data.accountEnabled : true}
                  render={({ field }) => (
                    <FormControlLabel
                      control={(
                        <Switch
                          {...field}
                          checked={!!field.value}
                          color="primary"
                        />
                      )}
                      label="Cuenta Habilitada"
                      labelPlacement="start"
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button type="submit" color="primary">
              Guardar
            </Button>
          </DialogActions>
        </form>
        {employees.loading && <LinearProgress color="primary" />}
      </Dialog>
    </div>
  );
};

NewUserScreen.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
};

export default NewUserScreen;
